/**
 * Phase BB7-1 — translate backend-supplied PAJE description strings.
 *
 * `paje.description` is produced by the backend in English ("Prior year
 * reconciliation: ROU_GROSS variance — tool X vs client Y, difference Z",
 * "USD Liability Revaluation Adjustment (Closing Rate: X vs Recorded:
 * Y)"). The backend cannot be changed without redeploying; we parse the
 * known patterns on the frontend and reformat through the active locale.
 * Unknown patterns pass through verbatim so a new backend description
 * shape never disappears — it just stays English until a key is added.
 */

/**
 * Minimal i18next-compatible `t` signature. Mirrors the local TFn used
 * in `journalEntryComponents.tsx` so the helper can be invoked from any
 * caller with a translation hook without coupling to react-i18next's
 * generic `TFunction` overloads.
 */
export type TFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * MINI_FIX_BS — exported so `buildPajeStory.ts` can reuse the SAME
 * regexes / account-key mapping without duplicating brittle parsing.
 * Adding a new caller never means writing a new regex.
 */
export const PRIOR_RECON_PATTERN =
  /^Prior year reconciliation: (\S+) variance — tool ([\d.]+) vs client ([\d.]+), difference ([-\d.]+)$/;

export const FX_REVALUATION_PATTERN =
  /^(\w+) Liability Revaluation Adjustment \(Closing Rate: ([\d.]+) vs Recorded: ([\d.]+)\)$/;

export function translateAccountKey(key: string, t: TFn): string {
  const map: Record<string, string> = {
    ROU_GROSS: t("paje.account.rouGross", { defaultValue: key }),
    ROU_ACC_DEP: t("paje.account.rouAccDep", { defaultValue: key }),
    LIABILITY_ST: t("paje.account.liabilitySt", { defaultValue: key }),
    LIABILITY_LT: t("paje.account.liabilityLt", { defaultValue: key }),
  };
  return map[key] ?? key;
}

export function translatePajeDescription(desc: string, t: TFn): string {
  const priorMatch = desc.match(PRIOR_RECON_PATTERN);
  if (priorMatch) {
    const [, accountKey, toolVal, clientVal, diff] = priorMatch;
    const account = translateAccountKey(accountKey!, t);
    return t("paje.description.priorRecon", {
      account,
      toolVal,
      clientVal,
      diff,
    });
  }

  const fxMatch = desc.match(FX_REVALUATION_PATTERN);
  if (fxMatch) {
    const [, currency, closingRate, recordedRate] = fxMatch;
    return t("paje.description.fxRevaluation", {
      currency,
      closingRate,
      recordedRate,
    });
  }

  return desc;
}
